"use client";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Upload,
  FileText,
  Shield,
  Truck,
  User,
  MapPin,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FleetInformation } from "./FleetInformation";
import { ServiceAreaAndSpecializations } from "./ServiceAreaAndSpecializations";
import { CompanyInfoCard } from "./CompanyInfoCard";
import { PrimaryContactCard } from "./PrimaryContactCard";

interface UploadedFile {
  name: string;
  size: string;
  uploadedAt: string;
  status: "uploaded" | "pending" | "rejected";
}

export interface CarrierFormData {
  // Company Information
  companyName: string;
  dotNumber: string;
  mcNumber: string;
  federalTaxId: string;
  businessAddress: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  email: string;
  website: string;

  // Contact Information
  primaryContactName: string;
  primaryContactTitle: string;
  primaryContactPhone: string;
  primaryContactEmail: string;

  // Fleet Information
  numberOfDrivers: number;
  numberOfVehicles: number;
  vehicleTypes: string[];
  operatingRadius: string;

  // Insurance & Compliance
  liabilityLimit: string;
  cargoLimit: string;
  insuranceCarrier: string;
  insuranceExpiryDate: string;
  safetyRating: string;

  // Vehicle Limits
  maxWeight: string;
  maxLength: string;
  maxWidth: string;
  maxHeight: string;

  // Service Areas
  serviceStates: string[];
  specializations: string[];
}

const requiredDocuments = [
  {
    id: "certificate_of_insurance",
    name: "Certificate of Insurance",
    description: "Current certificate showing liability and cargo coverage",
    required: true,
  },
  {
    id: "authority_certificate",
    name: "Operating Authority Certificate",
    description: "MC authority certificate from FMCSA",
    required: true,
  },
  {
    id: "w9_form",
    name: "W-9 Tax Form",
    description: "Completed W-9 form for tax reporting",
    required: true,
  },
  {
    id: "safety_rating",
    name: "Safety Rating Documentation",
    description: "Current DOT safety rating certificate",
    required: true,
  },
];

export const usStates = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export default function CarrierSignup() {
  const [formData, setFormData] = useState<CarrierFormData>({
    companyName: "",
    dotNumber: "",
    mcNumber: "",
    federalTaxId: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
    website: "",
    primaryContactName: "",
    primaryContactTitle: "",
    primaryContactPhone: "",
    primaryContactEmail: "",
    numberOfDrivers: 0,
    numberOfVehicles: 0,
    vehicleTypes: [],
    operatingRadius: "",
    liabilityLimit: "",
    cargoLimit: "",
    insuranceCarrier: "",
    insuranceExpiryDate: "",
    safetyRating: "",
    maxWeight: "",
    maxLength: "",
    maxWidth: "",
    maxHeight: "",
    serviceStates: [],
    specializations: [],
  });

  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, UploadedFile>
  >({});
  const [currentStep, setCurrentStep] = useState(0);

  const handleInputChange = (field: keyof CarrierFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field: keyof CarrierFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).includes(value)
        ? (prev[field] as string[]).filter((item) => item !== value)
        : [...(prev[field] as string[]), value],
    }));
  };

  const handleFileUpload = (documentId: string, file: File) => {
    const uploadedFile: UploadedFile = {
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      uploadedAt: new Date().toLocaleString(),
      status: "uploaded",
    };
    setUploadedFiles((prev) => ({ ...prev, [documentId]: uploadedFile }));
  };

  const removeFile = (documentId: string) => {
    setUploadedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[documentId];
      return newFiles;
    });
  };

  const getCompletionStatus = () => {
    const requiredDocs = requiredDocuments.filter((doc) => doc.required);
    const uploadedRequiredDocs = requiredDocs.filter(
      (doc) => uploadedFiles[doc.id]
    );
    const formCompletion = Object.values(formData).filter(
      (value) =>
        value !== "" &&
        value !== 0 &&
        (Array.isArray(value) ? value.length > 0 : true)
    ).length;
    const totalFormFields = Object.keys(formData).length;

    return {
      documents: `${uploadedRequiredDocs.length}/${requiredDocs.length}`,
      form: `${formCompletion}/${totalFormFields}`,
      documentsComplete: uploadedRequiredDocs.length === requiredDocs.length,
      formComplete: formCompletion >= totalFormFields * 0.8, // 80% completion
    };
  };

  const completionStatus = getCompletionStatus();

  const doc = requiredDocuments[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Carrier Registration
                </h1>
                <p className="text-gray-600">
                  Join our carrier network - Complete your application
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right text-sm">
                <div className="font-medium">Application Progress</div>
                <div className="text-gray-600">
                  Documents: {completionStatus.documents} | Form:{" "}
                  {completionStatus.form}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {completionStatus.documentsComplete &&
                completionStatus.formComplete ? (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-500" />
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="company" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="company">Company Info</TabsTrigger>
            <TabsTrigger value="fleet">Fleet & Operations</TabsTrigger>
            <TabsTrigger value="compliance">Insurance </TabsTrigger>
            <TabsTrigger value="documents">Document Upload</TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Company Information */}
              <CompanyInfoCard
                formData={formData}
                handleInputChange={handleInputChange}
              />
              {/* Primary Contact */}
              <PrimaryContactCard
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </TabsContent>

          <TabsContent value="fleet" className="space-y-6">
            <FleetInformation
              formData={formData}
              handleArrayToggle={handleArrayToggle}
              handleInputChange={handleInputChange}
            />
            {/* Service Areas & Specializations */}
            <ServiceAreaAndSpecializations
              formData={formData}
              handleArrayToggle={handleArrayToggle}
            />
          </TabsContent>

          <TabsContent value="compliance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Insurance Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Insurance Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="insuranceCarrier">
                      Insurance Carrier *
                    </Label>
                    <Input
                      id="insuranceCarrier"
                      value={formData.insuranceCarrier}
                      onChange={(e) =>
                        handleInputChange("insuranceCarrier", e.target.value)
                      }
                      placeholder="ABC Insurance Company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="liabilityLimit">
                      Liability Coverage Limit *
                    </Label>
                    <select
                      id="liabilityLimit"
                      value={formData.liabilityLimit}
                      onChange={(e) =>
                        handleInputChange("liabilityLimit", e.target.value)
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                      <option value="">Select Liability Limit</option>
                      <option value="$1,000,000">$1,000,000</option>
                      <option value="$2,000,000">$2,000,000</option>
                      <option value="$5,000,000">$5,000,000</option>
                      <option value="$10,000,000">$10,000,000</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cargoLimit">Cargo Coverage Limit *</Label>
                    <select
                      id="cargoLimit"
                      value={formData.cargoLimit}
                      onChange={(e) =>
                        handleInputChange("cargoLimit", e.target.value)
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                      <option value="">Select Cargo Limit</option>
                      <option value="$100,000">$100,000</option>
                      <option value="$250,000">$250,000</option>
                      <option value="$500,000">$500,000</option>
                      <option value="$1,000,000">$1,000,000</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="insuranceExpiryDate">
                      Insurance Expiry Date *
                    </Label>
                    <Input
                      id="insuranceExpiryDate"
                      type="date"
                      value={formData.insuranceExpiryDate}
                      onChange={(e) =>
                        handleInputChange("insuranceExpiryDate", e.target.value)
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Information */}
              <Card>
                <CardHeader>
                  <h4 className="font-medium">{doc.name}</h4>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div key={doc.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {uploadedFiles[doc.id] && (
                              <Badge variant="default">Uploaded</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {doc.description}
                          </p>
                        </div>
                      </div>

                      {uploadedFiles[doc.id] ? (
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <div className="font-medium text-green-900">
                                {uploadedFiles[doc.id].name}
                              </div>
                              <div className="text-sm text-green-700">
                                {uploadedFiles[doc.id].size} • Uploaded{" "}
                                {uploadedFiles[doc.id].uploadedAt}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(doc.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-600 mb-2">
                            Drag and drop your file here, or click to browse
                          </div>
                          <input
                            type="file"
                            id={`file-${doc.id}`}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(doc.id, file);
                              }
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              document.getElementById(`file-${doc.id}`)?.click()
                            }
                          >
                            Choose File
                          </Button>
                          <div className="text-xs text-gray-500 mt-2">
                            Supported formats: PDF, DOC, DOCX, JPG, PNG (Max
                            10MB)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Document Upload
                </CardTitle>
                <CardDescription>
                  Upload all required documents to complete your carrier
                  registration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {requiredDocuments.slice(-3).map((doc) => (
                    <div key={doc.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{doc.name}</h4>
                            {doc.required && (
                              <Badge variant="destructive">Required</Badge>
                            )}
                            {uploadedFiles[doc.id] && (
                              <Badge variant="default">Uploaded</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {doc.description}
                          </p>
                        </div>
                      </div>

                      {uploadedFiles[doc.id] ? (
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <div className="font-medium text-green-900">
                                {uploadedFiles[doc.id].name}
                              </div>
                              <div className="text-sm text-green-700">
                                {uploadedFiles[doc.id].size} • Uploaded{" "}
                                {uploadedFiles[doc.id].uploadedAt}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(doc.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-600 mb-2">
                            Drag and drop your file here, or click to browse
                          </div>
                          <input
                            type="file"
                            id={`file-${doc.id}`}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(doc.id, file);
                              }
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              document.getElementById(`file-${doc.id}`)?.click()
                            }
                          >
                            Choose File
                          </Button>
                          <div className="text-xs text-gray-500 mt-2">
                            Supported formats: PDF, DOC, DOCX, JPG, PNG (Max
                            10MB)
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Submit Application */}
            <Card>
              <CardHeader>
                <CardTitle>Submit Application</CardTitle>
                <CardDescription>
                  Review your information and submit your carrier application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {completionStatus.documents}
                      </div>
                      <div className="text-sm text-blue-800">
                        Required Documents
                      </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {completionStatus.form}
                      </div>
                      <div className="text-sm text-green-800">
                        Form Completion
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-900 mb-2">
                      Application Review Process
                    </h4>
                    <ul className="text-sm text-yellow-800 space-y-1">
                      <li>
                        • Your application will be reviewed within 2-3 business
                        days
                      </li>
                      <li>
                        • We may contact you for additional information or
                        clarification
                      </li>
                      <li>
                        • You'll receive an email notification once your
                        application is approved
                      </li>
                      <li>
                        • Approved carriers will receive access to our load
                        board
                      </li>
                    </ul>
                  </div>

                  <Button
                    size="lg"
                    className="w-full"
                    disabled={
                      !completionStatus.documentsComplete ||
                      !completionStatus.formComplete
                    }
                  >
                    Submit Carrier Application
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
